import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Inbox } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";
import { PageHeader, StatusBadge } from "@/components/crm/Badges";
import { ChannelIcon } from "@/components/crm/ChannelIcon";
import { ConversationThread } from "@/components/crm/conversations/ConversationThread";
import { Avatar } from "@/components/crm/conversations/MessageBubble";
import { useRealtime } from "@/hooks/useRealtime";

const TABS = [["todas", "Todas"], ["nuevas", "Nuevas"], ["pendientes", "Pendientes"], ["respondidas", "Respondidas"]];
const CHANNEL_TABS = ["WhatsApp", "Instagram", "Facebook", "Correo", "Web", "Otros"];


const Row = ({ c, active, onClick }) => (
  <button onClick={onClick} data-testid={`conversation-row-${c.id}`}
          className={`flex w-full gap-3 border-b border-slate-100 px-4 py-3.5 text-left transition-colors ${active ? "bg-[#1E6FD9]/[0.06]" : "hover:bg-[#F5F7FA]"}`}>
    <span className="relative"><Avatar name={c.lead_name} url={c.avatar_url} />
      {c.unread_count > 0 && <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-[#14B8A6] ring-2 ring-white" data-testid={`conversation-unread-${c.id}`} />}</span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center justify-between gap-2">
        <span className={`truncate text-sm ${c.status === "Nueva" ? "font-bold" : "font-semibold"}`}>{c.lead_name}</span>
        <span className="shrink-0 text-[11px] text-slate-400">{fmtDateTime(c.last_message_at)}</span>
      </span>
      <span className="mt-0.5 block truncate text-xs text-slate-500">{c.last_direction === "outbound" && "Tú: "}{c.last_message_preview}</span>
      <span className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1"><ChannelIcon channel={c.channel} className="h-3.5 w-3.5 text-[#1E6FD9]" /> {c.channel}</span>
        <span>· {c.seller_name || "Sin asignar"}</span>
        <StatusBadge status={c.status} testid={`conversation-status-${c.id}`} />
      </span>
    </span>
  </button>
);

export default function ConversationsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("todas");
  const [channel, setChannel] = useState("all");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(50);

  const load = useCallback(() => {
    const params = { ...(tab !== "todas" && { status: tab }), ...(channel !== "all" && { channel }), page: 1, page_size: limit };
    api.get("/conversations", { params }).then((r) => { setItems(r.data.items); setTotal(r.data.total); });
  }, [tab, channel, limit]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setLimit(50); }, [tab, channel]);
  useRealtime(() => load());

  return (
    <div data-testid="conversations-page">
      <PageHeader title="Conversaciones" subtitle="Bandeja omnicanal" />
      <div className="grid grid-cols-1 overflow-hidden rounded-2xl border border-slate-200/80 bg-white lg:h-[calc(100vh-200px)] lg:grid-cols-[380px_minmax(0,1fr)]">
        <div className={`flex min-h-0 min-w-0 flex-col border-slate-100 lg:border-r ${id ? "hidden lg:flex" : "flex"}`}>
          <div className="space-y-3 border-b border-slate-100 p-3">
            <div className="flex flex-wrap gap-1.5" data-testid="conversation-tabs">
              {TABS.map(([k, l]) => (
                <button key={k} onClick={() => { setTab(k); if (k === "todas") setChannel("all"); }} data-testid={`conversation-tab-${k}`}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${tab === k && (k !== "todas" || channel === "all") ? "bg-[#0B2545] text-white" : "bg-[#F5F7FA] text-slate-600"}`}>{l}</button>
              ))}
              {CHANNEL_TABS.map((c) => (
                <button key={c} onClick={() => setChannel(channel === c ? "all" : c)} data-testid={`conversation-channel-${c}`}
                        className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${channel === c ? "bg-[#1E6FD9] text-white" : "bg-[#F5F7FA] text-slate-600"}`}>{c}</button>
              ))}
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="conversation-list">
            {items.map((c) => <Row key={c.id} c={c} active={c.id === id} onClick={() => navigate(`/crm/conversaciones/${c.id}`)} />)}
            {items.length < total && (
              <button onClick={() => setLimit((n) => Math.min(n + 50, 500))} data-testid="conversations-load-more"
                      className="w-full py-3 text-xs font-semibold text-[#1E6FD9] transition-colors hover:bg-[#F5F7FA]">
                Cargar más ({total - items.length})
              </button>
            )}
            {items.length === 0 && (
              <div className="flex flex-col items-center py-16 text-slate-400" data-testid="conversations-list-empty">
                <Inbox className="h-8 w-8" /><p className="mt-2 text-sm">No hay conversaciones</p>
              </div>
            )}
          </div>
        </div>
        <div className={`min-h-[60vh] min-w-0 ${id ? "flex" : "hidden lg:flex"} flex-col`}>
          {id ? <ConversationThread id={id} onChanged={load} /> : (
            <div className="grid flex-1 place-items-center text-sm text-slate-400" data-testid="conversation-placeholder">Selecciona una conversación</div>
          )}
        </div>
      </div>
    </div>
  );
}
